'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { SearchResultDocument as SearchHit } from '@/lib/addsearch/types';
import { suggest } from '@/lib/addsearch';
import { SearchResults } from '../SearchResults/SearchResults';
import { searchBarVariants } from './SearchBar.styles';
import { setPendingSearchType, trackAdobeSearch } from '@/utils/adobe';

// ─── Constants ───────────────────────────────────────────────────────────────

const ADDSEARCH_API = 'https://api.addsearch.com/v1';
const DEBOUNCE_MS = 300;
const RESULT_LIMIT = 10;

// ─── Types ───────────────────────────────────────────────────────────────────

interface SearchApiResponse {
  hits: SearchHit[];
  total_hits: number;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface SearchBarProps {
  placeholder?: string;
  resultsPageUrl?: string;
  searchParameter?: string;
  showSubmitButton?: boolean;
  submitLabel?: string;
  isResultsPage?: boolean;
  /** Pre-populates the input; on results page, stays in sync with the URL param. */
  defaultValue?: string;
  /** Absolute base URL prepended to resultsPageUrl when used inside <srp-header /> on a non-SRP host. */
  redirectSearchBaseUrl?: string;
  /** Header desktop only: stretch the results dropdown to the header actions' right edge (past the search bar). */
  wideDropdown?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function SearchBar({
  placeholder = 'Search...',
  resultsPageUrl = '/search',
  searchParameter = 'search',
  showSubmitButton = false,
  submitLabel = 'Search',
  isResultsPage,
  defaultValue = '',
  redirectSearchBaseUrl = '',
  wideDropdown = false,
}: SearchBarProps) {
  const router = useRouter();
  const {
    container,
    form,
    inputWrapper,
    iconButton,
    icon,
    input,
    clearButton,
    spinner,
    submitButton,
    dropdown,
    suggestionItem,
  } = searchBarVariants({ wideDropdown });

  const [query, setQuery] = useState(defaultValue);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<{ value: string }[]>([]);
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [hasSearched, setHasSearched] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortRef = useRef<AbortController>(undefined);

  const siteKey = process.env.NEXT_PUBLIC_ADDSEARCH_SITE_KEY;

  // Keep input in sync with the URL-derived defaultValue (browser back/forward).
  useEffect(() => {
    if (isResultsPage) setQuery(defaultValue);
  }, [defaultValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset highlighted suggestion whenever the list changes or closes.
  useEffect(() => {
    setActiveSuggestionIndex(-1);
  }, [suggestions, isSuggestOpen]);

  // ── Fetch suggestions (results page only) ────────────────────────────────

  const fetchSuggestions = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSuggestions([]);
      setIsSuggestOpen(false);
      return;
    }
    try {
      const data = await suggest({ term });
      setSuggestions(data.suggestions ?? []);
      setIsSuggestOpen((data.suggestions ?? []).length > 0);
    } catch {
      setSuggestions([]);
      setIsSuggestOpen(false);
    }
  }, []);

  // ── Fetch results (header dropdown only) ─────────────────────────────────

  const fetchResults = useCallback(
    async (term: string) => {
      if (!siteKey || !term.trim()) {
        setHits([]);
        setIsOpen(false);
        return;
      }

      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          term,
          fuzzy: 'auto',
          collectAnalytics: 'false',
          page: '1',
          limit: String(RESULT_LIMIT),
          sort: 'relevance',
          order: 'desc',
          filter: '{}',
        });

        const res = await fetch(`${ADDSEARCH_API}/search/${siteKey}?${params}`, {
          signal: abortRef.current.signal,
        });

        if (!res.ok) throw new Error(`Search failed (${res.status})`);

        const data: SearchApiResponse = await res.json();
        setHits(data.hits ?? []);
        setIsOpen((data.hits ?? []).length > 0);
        setHasSearched(true);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setHits([]);
        setIsOpen(false);
        setHasSearched(true);
      } finally {
        setIsLoading(false);
      }
    },
    [siteKey]
  );

  // ── URL update helper (results page only) ────────────────────────────────

  const updateUrlQuery = useCallback(
    (term: string) => {
      const params = new URLSearchParams(window.location.search);
      if (term.trim()) {
        params.set(searchParameter, term);
      } else {
        params.delete(searchParameter);
      }
      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParameter]
  );

  // ── Input handlers ────────────────────────────────────────────────────────

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setHits([]);
      setIsOpen(false);
      setSuggestions([]);
      setIsSuggestOpen(false);
      setHasSearched(false);
      return;
    }

    if (isResultsPage) {
      debounceRef.current = setTimeout(() => fetchSuggestions(value), DEBOUNCE_MS);
    } else {
      debounceRef.current = setTimeout(() => fetchResults(value), DEBOUNCE_MS);
    }
  };

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!query.trim()) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setIsOpen(false);
    setIsSuggestOpen(false);
    if (isResultsPage) {
      setPendingSearchType('typed');
      updateUrlQuery(query);
    } else {
      // When rendered inside the standalone <srp-header /> on a non-SRP consumer site,
      // redirect to the absolute SRP results URL passed in via redirectSearchBaseUrl.
      window.location.href = `${redirectSearchBaseUrl}${resultsPageUrl}?${searchParameter}=${encodeURIComponent(query)}`;
    }
  };

  const handleSuggestionClick = (term: string) => {
    setQuery(term);
    setIsSuggestOpen(false);
    setPendingSearchType('suggested');
    updateUrlQuery(term);
  };

  const handleResultClick = () => {
    // Header dropdown only: a result click is a suggested search, so fire the Search event before clearing
    if (!isResultsPage) {
      trackAdobeSearch({ searchTerm: query, resultsValue: hits.length, searchType: 'suggested' });
    }
    setIsOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Close all dropdowns and release focus on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setIsSuggestOpen(false);
      setActiveSuggestionIndex(-1);
      return;
    }
    if (!isResultsPage || !isSuggestOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[activeSuggestionIndex].value);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleInputBlur = () => {
    setIsSuggestOpen(false);
    setActiveSuggestionIndex(-1);
  };

  // Prevent the input from losing focus when clicking inside the dropdown,
  // so onBlur doesn't fire and the suggestion onClick can complete.
  const handleSuggestMouseDown = (e: React.MouseEvent) => e.preventDefault();

  // ── Close on outside click ───────────────────────────────────────

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsSuggestOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, []);

  // ── Cleanup timers / in-flight requests on unmount ────────────────────────

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  // aria-controls must reference IDs present in the DOM, so mirror the render conditions below
  const isSuggestionsRendered = isResultsPage && isSuggestOpen && suggestions.length > 0;
  const isDropdownRendered = hasSearched && !isLoading && (isOpen || hits.length === 0);

  return (
    <div ref={containerRef} className={container()}>
      <form onSubmit={handleSubmit} className={form()}>
        <div className={inputWrapper()}>
          <input
            ref={inputRef}
            type="text"
            // Combobox semantics only apply to the results-page suggestions listbox. The header
            // popup is a list of navigable links (not selectable options), so it is a plain search
            // field that only associates its results region via aria-controls.
            role={isResultsPage ? 'combobox' : undefined}
            aria-expanded={isResultsPage ? isSuggestOpen : undefined}
            aria-autocomplete={isResultsPage ? 'list' : undefined}
            aria-haspopup={isResultsPage ? 'listbox' : undefined}
            aria-controls={
              isResultsPage
                ? isSuggestionsRendered
                  ? 'search-suggestions'
                  : undefined
                : isDropdownRendered
                  ? 'search-dropdown'
                  : undefined
            }
            aria-activedescendant={
              isResultsPage && activeSuggestionIndex >= 0
                ? `suggestion-${activeSuggestionIndex}`
                : undefined
            }
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => !isResultsPage && hits.length > 0 && setIsOpen(true)}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            aria-label="Search"
            autoComplete="off"
            className={input()}
          />
          {/* Real submit trigger for the form (WCAG); absolutely positioned over the magnifier icon area, after the input for natural tab order */}
          <button type="submit" className={iconButton()} aria-label={submitLabel}>
            <i className={icon()} aria-hidden="true"></i>
          </button>
          {isLoading ? (
            <span className={spinner()} />
          ) : (
            query && (
              <i
                className={clearButton()}
                onClick={() => {
                  setQuery('');
                  setHits([]);
                  setIsOpen(false);
                  setHasSearched(false);
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
              />
            )
          )}
        </div>
        {showSubmitButton && (
          <button type="submit" className={submitButton()}>
            {submitLabel}
          </button>
        )}
      </form>

      {isSuggestionsRendered && (
        <div className={dropdown()} onMouseDown={handleSuggestMouseDown}>
          <ul id="search-suggestions" role="listbox" aria-label="Search suggestions">
            {suggestions.map((item, idx) => (
              <li
                key={item.value}
                id={`suggestion-${idx}`}
                role="option"
                aria-selected={idx === activeSuggestionIndex}
              >
                <button
                  type="button"
                  className={suggestionItem({ suggestionActive: idx === activeSuggestionIndex })}
                  onClick={() => handleSuggestionClick(item.value)}
                >
                  {item.value}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isDropdownRendered && (
        <div id="search-dropdown" className={dropdown()}>
          <SearchResults hits={hits} query={query} onResultClick={handleResultClick} />
        </div>
      )}
    </div>
  );
}
